import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { ApigeeCfgService } from './apigee-cfg.service';
import { Router } from '@angular/router';

export class ApigeeToken {
  public access_token: string;
  public expires_at: number;
  public refresh_token: string;
}

export class ApigeeUserInfo {
  public sub: string;
  public nameid: string;
}

export class GoogleTokenExpiredError extends Error {
  constructor(message: string) {
    super(message)

    const actualProto = new.target.prototype;

    if (Object.setPrototypeOf) { Object.setPrototypeOf(this, actualProto); }
    else { (this as any).__proto__ = actualProto; }
  }
}

export class MissingGoogleTokenError extends Error {
  constructor() {
    super("Google token missing")

    const actualProto = new.target.prototype;

    if (Object.setPrototypeOf) { Object.setPrototypeOf(this, actualProto); }
    else { (this as any).__proto__ = actualProto; }
  }
}

const API_TOKEN_KEY = 'apiToken';
const GOOGLE_AUTH_TOKEN: string = 'googleAuth';
const GOOGLE_DIRECTORY_TOKEN: string = 'googleDirectory';
const BRANCH: string = "branch";

@Injectable({
  providedIn: 'root'
})
export class ApigeeService {

  private http = inject(HttpClient);
  private httpBackend = inject(HttpBackend);
  private rawHttpClient = new HttpClient(this.httpBackend);
  private cfg = inject(ApigeeCfgService);
  private router = inject(Router);

  private userInfoCache: ApigeeUserInfo = null;

  private refreshRequest$?: Observable<ApigeeToken>;

  private authTokensUpdatedSource = new Subject<void>();
  public authTokensUpdated = this.authTokensUpdatedSource.asObservable();

  private authInfoUserSource = new Subject<void>();
  public authInfoUserUpdated = this.authInfoUserSource.asObservable();

  /**
   * Obtiene el token desde localStorage
   */
  get apiToken(): ApigeeToken | null {
    const raw = localStorage.getItem(API_TOKEN_KEY);
    if (!raw || raw === 'null' || raw === 'undefined') return null;
    return JSON.parse(raw);
  }

  /**
   * Obtiene la informacion del usuario logueado
   */
  get userInfo(): ApigeeUserInfo | null {
    return this.userInfoCache;
  }

  /**
   * Verifica si existe el token en localStorage
   */
  public get hasToken(): boolean {
    let token = localStorage.getItem(API_TOKEN_KEY);
    return token !== undefined && token !== null && token !== 'null' && token !== 'undefined';
  }

  /**
   * Guarda el token en localStorage
   * @param token Token a guardar
   */
  private storeToken(token: ApigeeToken): void {
    localStorage.setItem(API_TOKEN_KEY, JSON.stringify(token));
  }

  /**
   * Guarda la informacion de Google en localStorage
   * @param googleAuth Datos del token
   * @param googleDirectory Informacion del usuario de Google
   */
  public storeGoogleTokens(googleAuth: any, googleDirectory: any): void {
    localStorage.setItem(GOOGLE_AUTH_TOKEN, googleAuth ? JSON.stringify(googleAuth) : null);
    localStorage.setItem(GOOGLE_DIRECTORY_TOKEN, googleDirectory ? JSON.stringify(googleDirectory) : null);
  }

  /**
   * Obtiene la informacion del token de Google desde localStorage
   */
  public get googleAuthToken(): any {
    let googleAuth = localStorage.getItem(GOOGLE_AUTH_TOKEN);
    if (googleAuth !== undefined && googleAuth !== null && googleAuth !== 'null' && googleAuth !== 'undefined' && googleAuth !== '{"error":"invalid_grant"}') {
      return JSON.parse(googleAuth);
    } else return null;
  }

  /**
   * Obtiene la informacion del usuario de Google desde localStorage
   */
  public get googleDirectoryToken(): any {
    let googleDirectory = localStorage.getItem(GOOGLE_DIRECTORY_TOKEN);
    if (googleDirectory !== undefined && googleDirectory !== null && googleDirectory !== 'null' && googleDirectory !== 'undefined' && googleDirectory !== '{"error":"invalid_grant"}') {
      return JSON.parse(googleDirectory);
    } else return null;
  }

  /**
   * Limpia las credencial del token
   */
  clearToken(): void {
    this.clearCredentials();
    this.userInfoCache = null;
  }

  /**
   * Verifica si esta vencido el token o por vencer en un margen de 3 minutos(180 segundos)
   * @returns true si se vencio el token
   */
  shouldRefresh(): boolean {
    const expiresAt = this.apiToken?.expires_at;

    if (!expiresAt) {
      return true;
    }

    const now = Math.trunc(Date.now() / 1000);

    return now > (expiresAt - 3600); // bajar a 180 en refresh
  }

  /**
   * Determina si el token es valido
   * @returns mensaje vacio si todo es correcto
   */
  isTokenValid(): string {
    let tokenError = '';

    if (this.userInfoCache) {
      if (this.userInfo.nameid == undefined || this.userInfo.nameid == null || this.userInfo.nameid == '') {
        tokenError = 'El perfil de Google no tiene la información necesaria para acceder a FBi. Informa por NACS área Servidesk IT';
      }

      // if (!this.validRoles(decodedPayload.role)) {
      //   tokenError = 'Su usuario no tiene autorización para acceder a FBi (configuración de Gestor). Informa por NACS área Servidesk IT';
      // }
    }

    return tokenError;
  }

  /**
   * llama al Api para obtener run token
   * @param email Correo del usuario
   * @param googleAccessToken Token de google
   * @returns Token de ApiGee
   */
  requestToken(email: string, googleAccessToken: string): Observable<ApigeeToken> {
    const cfg = this.cfg.getCfg();
    const body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('client_Id', cfg.clientId);
    body.set('user_id', email);
    body.set('g_token', googleAccessToken);

    return this.rawHttpClient.post<ApigeeToken>(cfg.tokenEndpoint, body.toString(), {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    }).pipe(
      tap(token => {
        this.storeToken(token);
        this.authTokensUpdatedSource.next();
      })
    );
  }

  /**
   * llamada al Api para realizar un refresco del token
   * @returns Token nuevo
   */
  refreshToken(): Observable<ApigeeToken> {
    // Ya existe un refresh ejecutándose
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const current = this.apiToken;
    if (!current?.refresh_token) return throwError(() => new Error('No refresh_token available'));

    const cfg = this.cfg.getCfg();
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', current.refresh_token);

    this.refreshRequest$ = this.rawHttpClient.post<ApigeeToken>(cfg.tokenEndpoint, body.toString(), {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    }).pipe(
      tap(token => this.storeToken(token)),

      shareReplay(1),

      finalize(() => {
        this.refreshRequest$ = undefined;
      })
    );

    return this.refreshRequest$;
  }

  /**
   * Llamada al Api para revocar el token
   * @returns Estado de la petición
   */
  revokeToken(): Observable<void> {
    const current = this.apiToken;
    if (!current) {
      return of(void 0);
    }

    const cfg = this.cfg.getCfg();
    const body = new URLSearchParams();
    body.set('token', current.access_token);
    body.set('token_type_hint', 'access_token');

    return this.http.post<void>(cfg.revokeEndpoint, body.toString(), {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    }).pipe(
      tap(() => this.clearToken()),
      catchError(() => { this.clearToken(); return of(void 0); }) // best-effort
    );
  }

  /**
   * Llamada al Api para obtener información del usuario logueado
   * @returns Información del usuario
   */
  getUserInfoApi(): Observable<ApigeeUserInfo> {
    if (this.userInfoCache) return of(this.userInfoCache);

    const token = this.apiToken;
    if (!token) return throwError(() => new Error('No token'));

    return this.http.get<ApigeeUserInfo>(this.cfg.getCfg().userInfoEndpoint, {
      headers: new HttpHeaders({ Authorization: `Bearer ${token.access_token}` })
    }).pipe(
      tap(info => {
        this.userInfoCache = info
        this.authInfoUserSource.next();
      })
    );
  }

  /**
   * Verifica si existe un token valido y si nno refresca el token
   * @returns valid token
   */
  ensureValidToken(): Observable<boolean> {
    const token = this.apiToken;
    if (!token) return of(false);

    if (!this.shouldRefresh()) return of(true);

    this.clearToken();
    return of(false);

    /// Comentado el proceso de refresh token

    // return this.refreshToken().pipe(
    //   map(() => true),
    //   catchError(() => { this.clearToken(); return of(false); })
    // );
  }

  /**
   * Obtiene la imagen de perfil del usuario
   * @returns Imagen de perfil del usuario
   */
  public getProfileImage(): string {
    let gtoken = this.googleAuthToken;
    let pictureUrl: string = "";
    if (gtoken) {
      let token = gtoken.id_token;
      if (token.indexOf('.') > 0) {
        //tomamos la segunda parte del token
        let payLoadData = token.split('.')[1];
        //decodificamos
        let plData = JSON.parse(payLoadData.atob());
        pictureUrl = plData.picture;
      }
    }
    return pictureUrl;
  }

  /**
   * Obtiene el nombre del usuario
   * @returns Nombre del usuario
   */
  public getUserDisplayName(): string {
    let gtoken = this.googleAuthToken;
    let name = null;
    if (gtoken) {
      let token = gtoken.id_token;
      if (token.indexOf('.') > 0) {
        //tomamos la segunda parte del token
        let payLoadData = token.split('.')[1];
        //decodificamos
        let plData = JSON.parse(payLoadData.atob());
        name = plData.name;
      }
    }
    return name;
  }

  /**
   * Realiza un deslogueo de la aplicación
   */
  logout(): void {
    this.clearCredentials();
    this.router.navigate(['login']);
  }

  /**
   * Limpia las credenciales
   */
  private clearCredentials(): void {
    localStorage.removeItem(API_TOKEN_KEY);
    localStorage.removeItem(GOOGLE_AUTH_TOKEN);
    localStorage.removeItem(GOOGLE_DIRECTORY_TOKEN);
    localStorage.removeItem(BRANCH);
  }
}
